import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { actions as commonActions } from "../store/commonRedux/actions";
import { shouldShowOnBoarding } from "../utils/helpers";
import { tourFlagForPath } from "../utils/tourFlags";

/* Owns when a tour opens: the first-run walkthrough, the header's "replay this
 * page" button, and the manual "i" buttons.
 *
 * Two bugs this replaces:
 *
 * 1. Replay used to work by flipping the page's onboarding flag to false, with
 *    each page watching that flag. On a page whose tour had never been seen the
 *    flag was already false, so nothing changed, the effect never re-ran and the
 *    button did nothing — which is why only the dashboard and the project page
 *    (the two tours everyone has actually seen) appeared to work. Replay is now
 *    driven by `tourReplay` plus a nonce, so it fires regardless of the flag and
 *    fires again on every press.
 *
 * 2. Sub-tours fired from a one-shot timer guarded by `!document.querySelector(
 *    ".ant-tour")`. If the page tour was still on screen they marked themselves
 *    as seen and never opened. They now wait their turn, and `order` makes the
 *    sequence deterministic: the project page tour (0) runs, then the board (1),
 *    then the views toolbar (2). */

const RETRY_MS = 400;
const MAX_WAIT_MS = 60000;

/* Tours that are eligible right now, so a later one can hold back for an
 * earlier one instead of the two racing on whoever's timer lands first. */
const pendingTours = new Map();

/* flag -> the replay nonce that tour has already answered.
 *
 * Module level, not a ref: the guard used to live on the component, so a tour
 * whose page unmounts — every tab switch inside a project — came back with a
 * clean ref, saw the same `tourReplay` still sitting in the store and opened
 * itself again. One press of the help button then meant a tour on every visit
 * to the tab, forever. */
const firedNonces = new Map();

/* The notification prompt holds the screen the same way a tour does: it can
 * arrive at any point once the user has been in for five minutes, so a tour
 * mounting behind it waits rather than masking a modal the user still has to
 * answer. */
const notifPromptOpen = () =>
  Boolean(
    document.querySelector(
      ".notif-permission-modal:not([style*='display: none'])",
    ),
  );

const blockedByEarlierTour = (flag, order) => {
  for (const [otherFlag, otherOrder] of pendingTours) {
    if (otherFlag !== flag && otherOrder < order) return true;
  }
  return false;
};

export const useTourTrigger = (
  flag,
  { delay = 600, enabled = true, auto = true, order = 0, onOpen } = {},
) => {
  const { pathname } = useLocation();

  const onBoarded = useSelector(({ common }) => common[flag]);
  const replayFlag = useSelector(({ common }) => common.tourReplay);
  const replayNonce = useSelector(({ common }) => common.tourReplayNonce);
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);

  /* A manual open must not be cancelled by the auto-open bookkeeping, and it
   * ignores the "already seen" flag — that is the point of the "i" button. */
  const openedManually = useRef(false);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  // a replay targets every tour on the page, not just the one named by the flag
  const isReplay = Boolean(replayFlag) && replayFlag === tourFlagForPath(pathname);

  useEffect(() => {
    if (!enabled) return undefined;
    /* `auto: false` means "this page has a walkthrough, but it does not open
     * itself". Only three tours introduce themselves — the dashboard, the
     * project page and the new-project form — because those are the screens a
     * new user actually lands on. Everything else waits to be asked for, via
     * the header's «راهنمای این صفحه» button, which is why this check sits
     * after the replay case rather than next to `enabled`: a replay opens every
     * tour on the page regardless. */
    if (!auto && !isReplay) return undefined;
    if (isReplay && firedNonces.get(flag) === replayNonce) return undefined;
    if (!isReplay && onBoarded) return undefined;

    pendingTours.set(flag, order);

    let timer;
    let waited = 0;

    const release = () => {
      pendingTours.delete(flag);
      clearTimeout(timer);
    };

    const attempt = () => {
      if (openedManually.current) return release();
      if (!shouldShowOnBoarding()) return release();

      if (
        document.querySelector(".ant-tour") ||
        notifPromptOpen() ||
        blockedByEarlierTour(flag, order)
      ) {
        waited += RETRY_MS;
        if (waited >= MAX_WAIT_MS) return release();
        timer = setTimeout(attempt, RETRY_MS);
        return undefined;
      }

      pendingTours.delete(flag);
      if (isReplay) firedNonces.set(flag, replayNonce);

      onOpenRef.current?.();
      setOpen(true);

      if (!onBoarded) {
        dispatch(commonActions.setCustomOnboarding({ [flag]: true }));
      }
      return undefined;
    };

    timer = setTimeout(attempt, delay);
    return release;
  }, [
    flag,
    onBoarded,
    enabled,
    auto,
    delay,
    order,
    isReplay,
    replayNonce,
    dispatch,
  ]);

  const openTour = () => {
    openedManually.current = true;
    setOpen(true);
  };

  /* A replay is spent once the last tour it woke has been closed: leaving
   * `tourReplay` in the store meant the next reload of the page replayed the
   * whole chain again, unasked. */
  const clearSpentReplay = () => {
    if (isReplay && pendingTours.size === 0) {
      dispatch(commonActions.setCustomOnboarding({ tourReplay: null }));
    }
  };

  const closeTour = () => {
    openedManually.current = false;
    clearSpentReplay();
    setOpen(false);
  };

  /* Drop-in for a plain `setIsOpen`, so callers keep their existing handlers.
   * Closing has to clear the manual flag or the auto-open stays suppressed for
   * the rest of the mount. */
  const setTourOpen = (next) => {
    if (next === false) {
      openedManually.current = false;
      clearSpentReplay();
    }
    setOpen(next);
  };

  return { open, openTour, closeTour, setOpen: setTourOpen };
};

export default useTourTrigger;
